import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getRsswSitemap from "@store/Store/Components/RsswSitemap/Handlers/GetRsswSitemap";
import getProductsYMMD from "@store/Store/Components/RsswYMMD/Handlers/getProductsYMMD";
import { useProductListYmmdPage } from "@store/Store/Components/RsswYMMD/Hooks/useYMMD";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

type Props = HasHistory & WidgetProps;

export interface CompatibleVehicleStyles {
    wrapper?: InjectableCss;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    link?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export interface SitemapCarsModel {
    year: string;
    make: string;
    model: string;
    diameter: string;
}

interface SitemapYearModel {
    year: string;
    cars: SitemapCarsModel[];
}

export const compatibleVehicleStyles: CompatibleVehicleStyles = {
    wrapper: {
        css: css`
            margin: 30px 0;
            border-bottom: 1px solid ${COLORS.border.base};

            .link {
                display: flex;
                color: ${COLORS.text.base};

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            {
                                values: [`${TYPOGRAPHY.size.base}`, null, `${TYPOGRAPHY.size.smaller}`, null, null],
                                properties: ["font-size"],
                            },
                            { values: ["5px", null, "0", null, null], properties: ["margin-bottom"] },
                        ],
                        mediaOptions.min,
                    )}

                &:hover,
                &:focus {
                    color: ${COLORS.link.base};
                    text-decoration: underline;
                }
            }
        `,
    },
    accordionSection: {
        headerProps: {
            css: css`
                button {
                    padding: 12px 0 10px !important;
                }
            `,
        },
        titleTypographyProps: {
            transform: "uppercase",
            css: css`
                margin: 0 10px;
            `,
        },
        panelProps: {
            css: css`
                padding: 0 10px 20px;
                border: 0;

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["1", "2", "3", "4", "5"], properties: ["column-count"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        mergeCss: true,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = compatibleVehicleStyles;

const RsswSitemap = ({ history }: Props) => {
    const dispatch = useDispatch();
    const [rsswSitemap, setRsswSitemap] = useState<any>({});
    const productListYmmdPage = useProductListYmmdPage();
    const [yearsToDisplay, setYearsToDisplay] = useState<any[]>([]);

    let mounted = true;

    useEffect(() => {
        dispatch(
            getRsswSitemap({
                onComplete(resultProps) {
                    if (mounted) {
                        setRsswSitemap(resultProps?.apiResult?.result);
                    }
                },
            }),
        );

        return () => {
            mounted = false;
        };
    }, []);

    const handleLoadElement = (yearToFind: string) => {
        setYearsToDisplay([
            ...yearsToDisplay,
            {
                year: yearToFind,
                cars: rsswSitemap.years.find((year: any) => year.year === yearToFind)?.cars,
            },
        ]);
    };

    const goToYmmdResults = (year: string, make: string, model: string, diameter: string) => {
        const ymmdUrl = `${productListYmmdPage}?year=${year}&make=${make}&model=${model}&diameter=${diameter}`;
        dispatch(getProductsYMMD({ year, make, model, diameter, key: "diameter" }));
        history.push(ymmdUrl);
    };

    const rsswSitemapResults = rsswSitemap?.years?.length;

    if (typeof rsswSitemapResults === "undefined" || rsswSitemapResults === 0) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Accordion {...styles.accordion} headingLevel={2}>
                {rsswSitemap.years.map((year: SitemapYearModel) => {
                    const carsToDisplay = yearsToDisplay.find(param => param.year === year.year)?.cars;

                    return (
                        <AccordionSection
                            key={year.year}
                            title={year.year}
                            onTogglePanel={() => handleLoadElement(year.year)}
                            {...styles.accordionSection}
                        >
                            {carsToDisplay !== undefined &&
                                year.cars.map((vehicle: any, index) => {
                                    const label = `${vehicle.make} ${vehicle.model} ${vehicle.diameter} ${translate(
                                        "RSSW_Diameter_Abbreviation",
                                    )}`;
                                    const key = `${vehicle.make}-${index}`;

                                    return (
                                        <a
                                            key={key}
                                            onClick={() =>
                                                goToYmmdResults(
                                                    year.year,
                                                    vehicle.make,
                                                    vehicle.model,
                                                    vehicle.diameter,
                                                )
                                            }
                                            className="link"
                                        >
                                            {label}
                                        </a>
                                    );
                                })}
                        </AccordionSection>
                    );
                })}
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(RsswSitemap),
    definition: {
        displayName: "Rssw Sitemap",
        group: "Common",
    },
};

export default widgetModule;
